import { Pipe, PipeTransform } from '@angular/core';
import {
    Activity,
    ActivityEngagement,
    ActivityExternalTask,
    ActivityPartyType,
} from '@wdx/clmi/clmi-swagger-gen';
import { ACTIVITY_ICON_MAP } from '@wdx/clmi/utils/constants';
import { PartyToAvatarPipe } from '@wdx/clmi/utils/pipes';
import { ACTIVITY_NOTES_FORM_ID, ActivityType } from '@wdx/shared/utils';
import { ACTIVITY_FORM_ID_MAP } from '../../constants/activity.constants';
import { ListItem } from '../../models/list-item.model';
import { ToDoItem } from '../../models/to-do-item.model';
import { ReferenceToAvatarPipe } from '../reference-to-avatar.pipe';

@Pipe({ name: 'activityAsToDoItem', standalone: false })
export class ActivityAsToDoItemPipe implements PipeTransform {
    transform(
        value:
            | (Activity & ActivityExternalTask)
            | (Activity & ActivityExternalTask)[],
    ): ToDoItem | ToDoItem[] {
        if (!value) {
            return;
        }

        const valueArray = Array.isArray(value) ? value : [value];

        const results = valueArray?.map((activity) =>
            activity.externalStatus
                ? this.mapActivityExternalTask(activity)
                : this.mapActivity(activity),
        );

        return Array.isArray(value) ? results : results[0];
    }

    private mapActivityExternalTask(activity: ActivityExternalTask): ToDoItem {
        return {
            id: activity.id,
            primaryInfo: activity.subject,
            avatars: [new PartyToAvatarPipe().transform(activity.createdBy)],
            attachments: undefined,
            tags: activity.regarding?.length,
            icon: ACTIVITY_ICON_MAP[ActivityType.ExternalTask],
            dueDate: new Date(activity.due || activity.createdOn),
            data: {
                activity,
                activityFormSetup: {
                    formId: ACTIVITY_FORM_ID_MAP[ActivityType.ExternalTask],
                    entityId: activity.id,
                },
                notesFormSetup: {
                    formId: ACTIVITY_NOTES_FORM_ID,
                    entityId: activity.id,
                },
            },
            unknownContacts: activity.activityParties?.filter(
                (party) => party.contactPoint && !party.party,
            ).length,
            filedUnder: [
                { label: activity.target?.name, data: activity.target },
            ],
            status: activity.externalStatus,
        };
    }

    private mapActivity(activity: Activity | ActivityEngagement): ToDoItem {
        const filedUnder = this.getFiledUnder(activity);

        return {
            id: activity.id,
            primaryInfo: activity.subject,
            content: activity.content,
            avatars: activity.activityParties
                ? activity.activityParties
                      .filter(
                          (m) =>
                              m.activityPartyType !== ActivityPartyType.Creator,
                      )
                      .map((activityParty) =>
                          activityParty.party
                              ? new PartyToAvatarPipe().transform(
                                    activityParty.party,
                                    undefined,
                                    undefined,
                                    undefined,
                                    undefined,
                                    activityParty.activityPartyType,
                                )
                              : new ReferenceToAvatarPipe().transform(
                                    activityParty.contactPoint,
                                ),
                      )
                : [],
            attachments:
                (activity.countFiles || 0) +
                (activity.documentAssociationCount || 0),
            tags: filedUnder.length,
            icon: ACTIVITY_ICON_MAP[activity.activityType],
            dueDate: new Date(activity.due || activity.createdOn),
            data: {
                activity,
                activityFormSetup: {
                    formId: ACTIVITY_FORM_ID_MAP[activity.activityType],
                    entityId: activity.id,
                },
                notesFormSetup: {
                    formId: ACTIVITY_NOTES_FORM_ID,
                    entityId: activity.id,
                },
            },
            unknownContacts: activity.activityParties?.filter(
                (party) => party.contactPoint && !party.party,
            ).length,
            filedUnder,
            status: activity.status,
            hasIssue: activity.hasIssue,
            commentCount: activity.commentCount,
            externalUrl: (activity as ActivityEngagement).conversationLink,
            conversationStatus: (activity as ActivityEngagement)
                .conversationStatus,
        };
    }

    private getFiledUnder(activity: Activity | ActivityEngagement): ListItem[] {
        if (activity.regarding?.length) {
            return activity.regarding.map((regarding) => {
                return {
                    label: regarding.name ?? regarding.type,
                    data: regarding,
                } as ListItem;
            });
        }

        if (activity.activityType !== ActivityType.Email) {
            return [];
        }

        const parties = activity.activityParties
            ?.map((activityParty) => activityParty.contactPoint?.party)
            .filter((party, index, allParties) => {
                if (!party) {
                    return false;
                }

                return (
                    allParties.findIndex(
                        (candidate) =>
                            candidate?.id === party.id &&
                            candidate?.type === party.type &&
                            candidate?.name === party.name,
                    ) === index
                );
            });

        return (
            parties?.map((party) => {
                return {
                    label: party.name ?? party.type,
                    data: party,
                } as ListItem;
            }) ?? []
        );
    }
}
